const allowedMoxieHosts = new Set([
  'hello.withmoxie.com',
  'clients.digitalcowboy.io',
]);

const verifiedMoxieDefaults = {
  discoveryFormEmbed:
    'https://hello.withmoxie.com/01/digital-cowboy/repupgrade-partner-discovery-2?inFrame=true',
  discoveryFormPublic:
    'https://clients.digitalcowboy.io/public/repupgrade-partner-discovery-2',
  discoverySchedulerEmbed:
    'https://hello.withmoxie.com/01/digital-cowboy/repupgrade-partner-discovery-call?inFrame=true',
  discoverySchedulerPublic:
    'https://clients.digitalcowboy.io/public/repupgrade-partner-discovery-call',
};

const getVerifiedMoxieUrl = (value) => {
  if (!value) return '';

  try {
    const url = new URL(value);

    if (url.protocol !== 'https:' || !allowedMoxieHosts.has(url.hostname)) {
      return '';
    }

    return url.toString();
  } catch {
    return '';
  }
};

const discoveryFormEmbedUrl = getVerifiedMoxieUrl(
  import.meta.env?.VITE_MOXIE_PARTNER_DISCOVERY_FORM_URL
) || verifiedMoxieDefaults.discoveryFormEmbed;

const discoveryFormPublicUrl = getVerifiedMoxieUrl(
  import.meta.env?.VITE_MOXIE_PARTNER_DISCOVERY_FORM_PUBLIC_URL
) || verifiedMoxieDefaults.discoveryFormPublic;

const discoverySchedulerEmbedUrl = getVerifiedMoxieUrl(
  import.meta.env?.VITE_MOXIE_PARTNER_DISCOVERY_SCHEDULER_URL
) || verifiedMoxieDefaults.discoverySchedulerEmbed;

const discoverySchedulerPublicUrl = getVerifiedMoxieUrl(
  import.meta.env?.VITE_MOXIE_PARTNER_DISCOVERY_SCHEDULER_PUBLIC_URL
) || verifiedMoxieDefaults.discoverySchedulerPublic;

export const agencyPricing = {
  fullStackTechnologyCostPerActiveLocationMonthly: 180,
  perAgentTechnologyCostPerActiveLocationMonthly: 60,
  whiteLabelImplementationInvestment: 4131,
  whiteLabelSupportAdminPercent: 1,
  whiteLabelAnnualLicense: 1500,
};

export const agencyLinks = {
  discoveryForm: {
    embed: discoveryFormEmbedUrl,
    public: discoveryFormPublicUrl,
  },
  discoveryScheduler: {
    embed: discoverySchedulerEmbedUrl,
    public: discoverySchedulerPublicUrl,
  },
};

export const calculatorConfig = {
  defaults: {
    clientCount: 25,
    adoptionPercentage: 20,
    averageLocationsPerClient: 1,
    coBrandedMonthlyRetailPerLocation: 360,
    whiteLabelMonthlyRetailPerLocation: 540,
    timeHorizonMonths: 12,
  },
  horizons: [3, 6, 12, 18, 24, 36],
  limits: {
    clientCount: { min: 0, max: 10000 },
    adoptionPercentage: { min: 0, max: 100 },
    averageLocationsPerClient: { min: 0, max: 1000 },
    coBrandedMonthlyRetailPerLocation: { min: 0, max: 100000 },
    whiteLabelMonthlyRetailPerLocation: { min: 0, max: 100000 },
  },
};

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const clamp = (value, min, max) => (
  Math.min(Math.max(toFiniteNumber(value), min), max)
);

const calculateMargin = (contribution, revenue) => (
  revenue > 0 ? (contribution / revenue) * 100 : 0
);

function calculateWhiteLabelCrossover({
  activatedLocations,
  coBrandedMonthlyClientRevenue,
  whiteLabelMonthlyClientRevenue,
  monthlyTechnologyCost,
  whiteLabelMonthlyAdministrationFee,
}) {
  if (activatedLocations <= 0) return null;

  let coBrandedCumulativeContribution = 0;
  let whiteLabelCumulativeContribution = 0;
  const cumulativeDifferences = [];

  for (let month = 1; month <= 120; month += 1) {
    coBrandedCumulativeContribution += (
      coBrandedMonthlyClientRevenue - monthlyTechnologyCost
    );

    let whiteLabelFixedCharges = 0;

    if (month === 1) {
      whiteLabelFixedCharges += agencyPricing.whiteLabelImplementationInvestment;
    }

    if (month === 1 || (month - 1) % 12 === 0) {
      whiteLabelFixedCharges += agencyPricing.whiteLabelAnnualLicense;
    }

    whiteLabelCumulativeContribution += (
      whiteLabelMonthlyClientRevenue
      - monthlyTechnologyCost
      - whiteLabelMonthlyAdministrationFee
      - whiteLabelFixedCharges
    );

    cumulativeDifferences.push(
      whiteLabelCumulativeContribution - coBrandedCumulativeContribution
    );
  }

  const crossoverIndex = cumulativeDifferences.findIndex(
    (difference, index) => (
      difference >= 0
      && cumulativeDifferences
        .slice(index)
        .every((futureDifference) => futureDifference >= 0)
    )
  );

  return crossoverIndex === -1 ? null : crossoverIndex + 1;
}

export function calculateAgencyOpportunity(values) {
  const clients = clamp(
    values.clientCount,
    calculatorConfig.limits.clientCount.min,
    calculatorConfig.limits.clientCount.max
  );
  const adoption = clamp(
    values.adoptionPercentage,
    calculatorConfig.limits.adoptionPercentage.min,
    calculatorConfig.limits.adoptionPercentage.max
  );
  const locationsPerClient = clamp(
    values.averageLocationsPerClient,
    calculatorConfig.limits.averageLocationsPerClient.min,
    calculatorConfig.limits.averageLocationsPerClient.max
  );
  const coBrandedClientPrice = clamp(
    values.coBrandedMonthlyRetailPerLocation,
    calculatorConfig.limits.coBrandedMonthlyRetailPerLocation.min,
    calculatorConfig.limits.coBrandedMonthlyRetailPerLocation.max
  );
  const whiteLabelClientPrice = clamp(
    values.whiteLabelMonthlyRetailPerLocation,
    calculatorConfig.limits.whiteLabelMonthlyRetailPerLocation.min,
    calculatorConfig.limits.whiteLabelMonthlyRetailPerLocation.max
  );
  const requestedHorizon = toFiniteNumber(values.timeHorizonMonths);
  const horizon = calculatorConfig.horizons.includes(requestedHorizon)
    ? requestedHorizon
    : calculatorConfig.defaults.timeHorizonMonths;

  const activatedClients = clients * (adoption / 100);
  const activatedLocations = activatedClients * locationsPerClient;
  const monthlyTechnologyCost =
    activatedLocations
    * agencyPricing.fullStackTechnologyCostPerActiveLocationMonthly;

  const coBrandedMonthlyClientRevenue =
    activatedLocations * coBrandedClientPrice;
  const coBrandedMonthlyGrossContribution =
    coBrandedMonthlyClientRevenue - monthlyTechnologyCost;
  const coBrandedHorizonClientRevenue =
    coBrandedMonthlyClientRevenue * horizon;
  const coBrandedHorizonTechnologyCost =
    monthlyTechnologyCost * horizon;
  const coBrandedHorizonGrossContribution =
    coBrandedHorizonClientRevenue - coBrandedHorizonTechnologyCost;

  const whiteLabelMonthlyClientRevenue =
    activatedLocations * whiteLabelClientPrice;
  const whiteLabelMonthlyAdministrationFee =
    monthlyTechnologyCost
    * (agencyPricing.whiteLabelSupportAdminPercent / 100);
  const whiteLabelMonthlyRecurringOperatingCost =
    monthlyTechnologyCost + whiteLabelMonthlyAdministrationFee;
  const whiteLabelMonthlyGrossContribution =
    whiteLabelMonthlyClientRevenue - whiteLabelMonthlyRecurringOperatingCost;
  const whiteLabelHorizonClientRevenue =
    whiteLabelMonthlyClientRevenue * horizon;
  const whiteLabelAnnualLicenseCharges =
    Math.ceil(horizon / 12) * agencyPricing.whiteLabelAnnualLicense;
  const whiteLabelHorizonRecurringOperatingCost =
    whiteLabelMonthlyRecurringOperatingCost * horizon;
  const whiteLabelHorizonTotalCost =
    whiteLabelHorizonRecurringOperatingCost
    + agencyPricing.whiteLabelImplementationInvestment
    + whiteLabelAnnualLicenseCharges;
  const whiteLabelHorizonGrossContribution =
    whiteLabelHorizonClientRevenue - whiteLabelHorizonTotalCost;

  const whiteLabelAdvantage =
    whiteLabelHorizonGrossContribution
    - coBrandedHorizonGrossContribution;
  const whiteLabelCrossoverMonth = calculateWhiteLabelCrossover({
    activatedLocations,
    coBrandedMonthlyClientRevenue,
    whiteLabelMonthlyClientRevenue,
    monthlyTechnologyCost,
    whiteLabelMonthlyAdministrationFee,
  });

  return {
    activatedClients,
    activatedLocations,
    horizon,
    coBranded: {
      clientPrice: coBrandedClientPrice,
      monthlyClientRevenue: coBrandedMonthlyClientRevenue,
      monthlyTechnologyCost,
      monthlyGrossContribution: coBrandedMonthlyGrossContribution,
      horizonClientRevenue: coBrandedHorizonClientRevenue,
      horizonTechnologyCost: coBrandedHorizonTechnologyCost,
      horizonGrossContribution: coBrandedHorizonGrossContribution,
      grossContributionMargin: calculateMargin(
        coBrandedHorizonGrossContribution,
        coBrandedHorizonClientRevenue
      ),
    },
    whiteLabel: {
      clientPrice: whiteLabelClientPrice,
      monthlyClientRevenue: whiteLabelMonthlyClientRevenue,
      monthlyTechnologyCost,
      monthlyAdministrationFee: whiteLabelMonthlyAdministrationFee,
      monthlyRecurringOperatingCost:
        whiteLabelMonthlyRecurringOperatingCost,
      monthlyGrossContribution: whiteLabelMonthlyGrossContribution,
      horizonClientRevenue: whiteLabelHorizonClientRevenue,
      horizonRecurringOperatingCost:
        whiteLabelHorizonRecurringOperatingCost,
      implementationInvestment:
        agencyPricing.whiteLabelImplementationInvestment,
      annualLicenseCharges: whiteLabelAnnualLicenseCharges,
      horizonTotalCost: whiteLabelHorizonTotalCost,
      horizonGrossContribution: whiteLabelHorizonGrossContribution,
      grossContributionMargin: calculateMargin(
        whiteLabelHorizonGrossContribution,
        whiteLabelHorizonClientRevenue
      ),
    },
    comparison: {
      whiteLabelAdvantage,
      whiteLabelCrossoverMonth,
    },
  };
}